import React from "react";

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: "row" | "column" | "row-reverse" | "column-reverse";
  spacing?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  align?: "start" | "center" | "end" | "stretch" | "baseline";
  justify?: "start" | "center" | "end" | "between" | "around" | "evenly";
  wrap?: boolean;
  divider?: boolean;
  fullWidth?: boolean;
}

/**
 * Arc-style Stack component
 * Replaces MUI Stack
 * 
 * Features:
 * - Flexible direction
 * - Configurable spacing
 * - Alignment and justification
 * - Optional wrap
 * - Optional dividers
 */
const Stack: React.FC<StackProps> = ({
  direction = "column",
  spacing = "md",
  align = "stretch",
  justify = "start",
  wrap = false,
  divider = false,
  fullWidth = false,
  children,
  className = "",
  ...props
}) => {
  // Direction styles
  const directionStyles = {
    row: "flex-row",
    column: "flex-col",
    "row-reverse": "flex-row-reverse",
    "column-reverse": "flex-col-reverse",
  };

  // Spacing styles (gap)
  const spacingStyles = {
    none: "gap-0",
    xs: "gap-1",
    sm: "gap-2",
    md: "gap-4",
    lg: "gap-6",
    xl: "gap-8",
    "2xl": "gap-12",
  };

  // Alignment styles
  const alignStyles = {
    start: "items-start",
    center: "items-center",
    end: "items-end",
    stretch: "items-stretch",
    baseline: "items-baseline",
  };

  // Justification styles
  const justifyStyles = {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly",
  };

  // Process children to add dividers if needed
  const processedChildren = React.useMemo(() => {
    if (!divider) return children;

    const childArray = React.Children.toArray(children);
    const isHorizontal = direction === "row" || direction === "row-reverse";

    return childArray.map((child, index) => (
      <React.Fragment key={index}>
        {child}
        {index < childArray.length - 1 && (
          <div
            className={`
              bg-arc-border
              ${isHorizontal ? "w-px h-auto self-stretch" : "h-px w-full"}
            `}
          />
        )}
      </React.Fragment>
    ));
  }, [children, divider, direction]);

  return (
    <div
      className={`
        flex
        ${directionStyles[direction]}
        ${spacingStyles[spacing]}
        ${alignStyles[align]}
        ${justifyStyles[justify]}
        ${wrap ? "flex-wrap" : "flex-nowrap"}
        ${fullWidth ? "w-full" : ""}
        ${className}
      `}
      {...props}
    >
      {processedChildren}
    </div>
  );
};

Stack.displayName = "Stack";

export default Stack;









